<template>
  <div class="max-w-2xl mx-auto px-4 py-6 sm:px-6 lg:px-8 space-y-6">
    <div class="flex items-center justify-between">
      <button @click="goBack" class="btn btn-secondary !p-2 !rounded-full">
        <span class="material-symbols-outlined">arrow_back</span>
      </button>
      <h1 class="text-xl font-bold text-slate-800 dark:text-slate-100">
        {{ isEditing ? 'Editar Investimento' : 'Criar Investimento' }}
      </h1>
      <button @click="handleSubmit" :disabled="isSubmitting" class="btn btn-primary min-w-[80px]">
        <span v-if="isSubmitting" class="material-symbols-outlined animate-spin text-lg">progress_activity</span>
        <span v-else>Salvar</span>
      </button>
    </div>

    <div class="panel p-5 sm:p-6">
      <form @submit.prevent="handleSubmit" class="space-y-6">
        <div>
          <label for="pNome" class="form-label">Nome do Investimento</label>
          <input id="pNome" v-model.trim="form.nome" type="text" required
                 placeholder="Ex: Investimento em Bitcoin" class="form-input" />
        </div>
        <div>
          <label for="pDesc" class="form-label">Descrição</label>
          <input id="pDesc" v-model.trim="form.descricao" type="text"
                 placeholder="Objetivos do grupo" class="form-input" />
        </div>
        <div v-if="!isEditing">
          <label for="pEmails" class="form-label">Convidar Membros (opcional)</label>
          <textarea id="pEmails" v-model="emailsMembrosRaw" rows="3"
                    placeholder="Separe os emails por vírgula ou espaço" class="form-input"></textarea>
          <p class="text-xs text-slate-500 mt-1">Ex.: ana@ex.com, joao@ex.com</p>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import axios from 'axios';

const route = useRoute();
const router = useRouter();

const API_BASE_URL = 'https://backend.controlease.com.br/api';
const authConfig = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const isSubmitting = ref(false);
const isEditing = computed(() => !!route.params.uuid);

const form = ref({ nome: '', descricao: '' });
const emailsMembrosRaw = ref('');

const fetchPortfolio = async () => {
  try {
    const { data } = await axios.get(`${API_BASE_URL}/investimentos-compartilhados/${route.params.uuid}`, authConfig());
    form.value.nome = data?.nome || '';
    form.value.descricao = data?.descricao || '';
  } catch (e) {
    alert('Não foi possível carregar o investimento.');
    router.push({ name: 'InvestimentosCompartilhados' });
  }
};

const handleSubmit = async () => {
  if (isSubmitting.value) return;
  isSubmitting.value = true;
  try {
    if (isEditing.value) {
      await axios.put(`${API_BASE_URL}/investimentos-compartilhados/${route.params.uuid}`, form.value, authConfig());
      router.push({ name: 'InvestimentosCompartilhados', params: { id: route.params.uuid } });
    } else {
      const emails = emailsMembrosRaw.value.split(/[\s,]+/).filter(Boolean);
      const payload = { ...form.value, emailsMembros: emails };
      const { data } = await axios.post(`${API_BASE_URL}/investimentos-compartilhados`, payload, authConfig());
      const novoUuid = data?.uuid;
      router.push({ name: 'InvestimentosCompartilhados', params: { id: novoUuid || undefined } });
    }
  } catch (e) {
    alert(e?.response?.data?.message || 'Erro ao salvar investimento.');
  } finally {
    isSubmitting.value = false;
  }
};

const goBack = () => {
  if (isEditing.value) {
    router.push({ name: 'InvestimentosCompartilhados', params: { id: route.params.uuid } });
  } else {
    router.push({ name: 'InvestimentosCompartilhados' });
  }
};

onMounted(() => { if (isEditing.value) fetchPortfolio(); });
</script>

<style scoped>
.panel { @apply bg-white/70 dark:bg-brand-800/60 backdrop-blur-lg ring-1 ring-black/5 dark:ring-white/10 rounded-2xl shadow-xl; }
.btn { @apply inline-flex items-center justify-center font-semibold py-2.5 px-5 rounded-xl transition-all duration-200 text-sm active:scale-95 disabled:opacity-60; }
.btn-primary { @apply btn bg-brand-600 hover:bg-brand-700 text-white; }
.btn-secondary { @apply btn bg-white/70 dark:bg-slate-800/50 text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 ring-1 ring-black/5 dark:ring-white/10; }
.form-input { @apply w-full px-3.5 py-2.5 text-sm rounded-xl bg-white dark:bg-slate-800/50 border border-slate-300 dark:border-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-brand-500; }
.form-label { @apply block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5; }
.animate-spin { animation: spin 1s linear infinite; } 
@keyframes spin { from { transform: rotate(0deg) } to { transform: rotate(360deg) } }
</style>
